import { createApi } from '@reduxjs/toolkit/query/react';

import { constructUrl } from '@app/api/utils';
import type { FilterObjectData, FilterOptions } from '@app/components/CustomFilter';

import fetchBaseQuery from '../fetchBaseQuery';
import type {
    CreateTermRequest,
    CreateTermResponse,
    ExtractTermsRequest,
    ExtractTermsResponse,
    GetTagsResponse,
    GetTermsResponse,
    SortOrder,
    TermCountResponse,
    UpdateTermRequest,
    UpdateTermResponse,
    UploadTermsResponse,
} from './types';

export * from './types';

export * from './types';

const glossaryTermTag = 'GlossaryTerm' as const;

const glossaryApi = createApi({
    reducerPath: 'lokaliseInternal_glossary',
    baseQuery: fetchBaseQuery(),
    tagTypes: [glossaryTermTag],
    endpoints: (builder) => ({
        getTermCount: builder.query<TermCountResponse, { projectId: string }>({
            query: ({ projectId }) => ({
                method: 'GET',
                url: `/glossary/${projectId}/count`,
            }),
        }),
        getTags: builder.query<GetTagsResponse, { projectId: string }>({
            query: ({ projectId }) => ({
                method: 'GET',
                url: `/glossary/${projectId}/tags`,
            }),
        }),
        extractTerms: builder.mutation<ExtractTermsResponse, { projectId: string; body: ExtractTermsRequest }>({
            query: ({ projectId, body }) => ({
                method: 'POST',
                url: `/glossary/${projectId}/extract-terms`,
                body,
            }),
            invalidatesTags: [{ type: glossaryTermTag, id: 'LIST' }],
        }),
        uploadTerms: builder.mutation<UploadTermsResponse, { projectId: string; body: FormData }>({
            query: ({ projectId, body }) => ({
                method: 'POST',
                url: `/glossary/${projectId}/upload/csv`,
                headers: {
                    'x-auto-content-type': '1',
                },
                body,
            }),
            invalidatesTags: [{ type: glossaryTermTag, id: 'LIST' }],
        }),
        getTermFilterOptions: builder.query<FilterOptions[], { projectId: string }>({
            query: ({ projectId }) => ({
                method: 'GET',
                url: `/glossary/${projectId}/filter-data`,
            }),
        }),
        getTerms: builder.query<
            GetTermsResponse,
            {
                projectId: string;
                query?: string;
                caseSensitiveSearch?: boolean;
                pageAfter?: string;
                sort?: SortOrder;
                filters?: readonly FilterObjectData[];
            }
        >({
            query: ({ projectId, filters, ...params }) => ({
                method: 'GET',
                url: constructUrl(`/glossary/${projectId}/term`, {
                    filters: filters?.map((filter) => JSON.stringify(filter)),
                    ...params,
                }),
            }),
            providesTags: (result, err, args) => {
                const resultTags =
                    !err && result ? result.data.map((term) => ({ type: glossaryTermTag, id: term.id })) : [];

                // Reason we have separate ids for list and page is that after mutation we want to force refresh
                // of initial list. But if we've loaded more than 1 page, there would be 2 separate requests.
                return args.pageAfter
                    ? [...resultTags, { type: glossaryTermTag, id: `PAGE_${args.pageAfter}` }]
                    : [...resultTags, { type: glossaryTermTag, id: 'LIST' }];
            },
        }),
        createTerm: builder.mutation<CreateTermResponse, { projectId: string } & CreateTermRequest>({
            query: ({ projectId, ...body }) => ({
                method: 'POST',
                url: `/glossary/${projectId}/term`,
                body,
            }),
            invalidatesTags: [{ type: glossaryTermTag, id: 'LIST' }],
        }),
        updateTerm: builder.mutation<UpdateTermResponse, { projectId: string; termId: string } & UpdateTermRequest>({
            query: ({ projectId, termId, ...body }) => ({
                method: 'PATCH',
                url: `/glossary/${projectId}/term/${termId}`,
                body,
            }),
            invalidatesTags: [{ type: glossaryTermTag, id: 'LIST' }],
            onQueryStarted: async ({ projectId }, { queryFulfilled, dispatch }) => {
                try {
                    const { data } = await queryFulfilled;
                    dispatch(
                        // After created, we add the new term to the top of the default list
                        glossaryApi.util.updateQueryData(
                            'getTerms',
                            { projectId, query: '', pageAfter: undefined },
                            (draft) => {
                                draft.data.forEach((term) => {
                                    if (term.id === data.term.id) {
                                        Object.assign(term, data.term);
                                    }
                                });
                            },
                        ),
                    );
                } catch {
                    // ignore
                }
            },
        }),
        deleteTerm: builder.mutation<unknown, { projectId: string; termId: string }>({
            query: ({ projectId, termId }) => ({
                method: 'DELETE',
                url: `/glossary/${projectId}/term/${termId}`,
            }),
            invalidatesTags: [{ type: glossaryTermTag, id: 'LIST' }],
            onQueryStarted: async ({ projectId, termId }, { queryFulfilled, dispatch }) => {
                try {
                    await queryFulfilled;
                    dispatch(
                        // After created, we add the new term to the top of the default list
                        glossaryApi.util.updateQueryData(
                            'getTerms',
                            { projectId, query: '', pageAfter: undefined },
                            (draft) => {
                                draft.data = draft.data.filter((term) => term.id !== termId);
                                draft.page.total -= 1;
                                draft.page.remaining -= 1;
                            },
                        ),
                    );
                } catch {
                    // ignore
                }
            },
        }),
        deleteAllTerms: builder.mutation<unknown, { projectId: string }>({
            query: ({ projectId }) => ({
                method: 'POST',
                url: `/glossary/${projectId}/delete-all`,
            }),
            invalidatesTags: [{ type: glossaryTermTag, id: 'LIST' }],
            onQueryStarted: async ({ projectId }, { queryFulfilled, dispatch }) => {
                try {
                    await queryFulfilled;
                    dispatch(
                        // After created, we add the new term to the top of the default list
                        glossaryApi.util.updateQueryData(
                            'getTerms',
                            { projectId, query: '', pageAfter: undefined },
                            (draft) => {
                                draft.data = [];
                                draft.page.total = 0;
                                draft.page.size = 0;
                                draft.page.remaining = 0;
                                draft.page.nextCursor = '';
                            },
                        ),
                    );
                } catch {
                    // ignore
                }
            },
        }),
    }),
});

export default glossaryApi;

export const {
    useGetTermCountQuery,
    useGetTagsQuery,
    useGetTermsQuery,
    useCreateTermMutation,
    useUpdateTermMutation,
    useDeleteTermMutation,
    useDeleteAllTermsMutation,
    useExtractTermsMutation,
    useUploadTermsMutation,
    useGetTermFilterOptionsQuery,
} = glossaryApi;
